import type { ReactNode } from 'react';

import { cn } from '@components/utils/classname';

export interface InformationRowProps {
  /**
   * Label text displayed on the left side
   */
  label: string;
  /**
   * Value displayed on the right side
   * Can be a string, number, or React node for complex content (including icons)
   */
  value: ReactNode;
  /**
   * Optional secondary text displayed below the value
   * Useful for showing USD value, additional info, etc.
   */
  secondaryValue?: ReactNode;
  /**
   * Alignment of items (default: 'center')
   * - 'center': Vertically center-aligned (for single-line values)
   * - 'start': Top-aligned (for multi-line values or with secondaryValue)
   */
  align?: 'center' | 'start';
  /**
   * Additional CSS classes for the container
   */
  className?: string;
  /**
   * Additional CSS classes for the label
   */
  labelClassName?: string;
  /**
   * Additional CSS classes for the value
   */
  valueClassName?: string;

  /**
   * Size of the information row
   * - 'sm': Small size
   * - 'md': Medium size
   * default: Medium size
   */
  size?: 'sm' | 'md';
}

/**
 * A reusable information row component that displays a label-value pair
 * Used in review/summary screens to show account details
 *
 * @example
 * // Simple text value
 * <InformationRow label="Network" value="Bitcoin" />
 *
 * @example
 * // With icon in value
 * <InformationRow
 *   label="Coin"
 *   value={
 *     <div className="flex items-center gap-1">
 *       <Coin symbol="BTC" size="sm" />
 *       <span>BTC</span>
 *     </div>
 *   }
 * />
 *
 * @example
 * // With secondary value (e.g., USD amount)
 * <InformationRow
 *   label="Amount received"
 *   value={<><span>9.9994</span> <span className="text-muted-foreground">BTC</span></>}
 *   secondaryValue="1,049,567.91 USD"
 *   align="start"
 * />
 */
export function InformationRow({
  label,
  value,
  secondaryValue,
  align = 'center',
  className,
  labelClassName,
  valueClassName,
  size = 'md',
}: Readonly<InformationRowProps>) {
  const hasSecondaryValue = !!secondaryValue;
  const alignItems =
    align === 'start' || hasSecondaryValue ? 'items-start' : 'items-center';

  return (
    <div
      className={cn(
        'flex min-h-6 justify-between px-0 py-1',
        alignItems,
        className,
      )}
    >
      <span
        className={cn(
          'text-secondary-foreground',
          size === 'sm' ? 'text-body2' : 'text-body1',
          labelClassName,
        )}
      >
        {label}
      </span>
      <div
        className={cn(
          'flex flex-col items-end gap-0',
          hasSecondaryValue && 'gap-0',
        )}
      >
        <div
          className={cn(
            'text-body1 text-foreground text-right',
            valueClassName,
          )}
        >
          {value}
        </div>
        {secondaryValue && (
          <span className="text-body-mono2 text-secondary-foreground text-right">
            {secondaryValue}
          </span>
        )}
      </div>
    </div>
  );
}

export interface InformationSectionProps {
  /**
   * Array of information items to display
   */
  items: Array<{
    label: string;
    value: ReactNode;
    secondaryValue?: ReactNode;
    align?: 'center' | 'start';
    className?: string;
    labelClassName?: string;
    valueClassName?: string;
  }>;
  /**
   * Additional CSS classes for the container
   */
  className?: string;
  /**
   * Spacing between items (default: 'gap-1' which is 4px)
   * - 'gap-0': 0px
   * - 'gap-1': 4px
   * - 'gap-2': 8px
   * - 'gap-3': 12px
   * - 'gap-4': 16px
   */
  spacing?: 'gap-0' | 'gap-1' | 'gap-2' | 'gap-3' | 'gap-4';

  /**
   * Size of the information section
   * - 'sm': Small size
   * - 'md': Medium size
   * default: Medium size
   */
  size?: 'sm' | 'md';
}

/**
 * A reusable information section component that displays multiple information rows
 * Used to group related information together
 *
 * @example
 * <InformationSection
 *   items={[
 *     { label: 'Network', value: 'Bitcoin' },
 *     {
 *       label: 'Coin',
 *       value: (
 *         <div className="flex items-center gap-1">
 *           <Coin symbol="BTC" size="sm" />
 *           <span>BTC</span>
 *         </div>
 *       ),
 *     },
 *     {
 *       label: 'Amount received',
 *       value: '9.9994 BTC',
 *       secondaryValue: '1,049,567.91 USD',
 *     },
 *   ]}
 * />
 */
export function InformationSection({
  items,
  className = '',
  spacing = 'gap-1',
  size = 'md',
}: Readonly<InformationSectionProps>) {
  return (
    <div className={cn('flex flex-col', spacing, className)}>
      {items.map((item, index) => (
        <InformationRow
          key={item.label || `item-${index}`}
          label={item.label}
          value={item.value}
          secondaryValue={item.secondaryValue}
          align={item.align}
          className={item.className}
          labelClassName={item.labelClassName}
          valueClassName={item.valueClassName}
          size={size}
        />
      ))}
    </div>
  );
}
